import flow from 'lodash/flow';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import qs from 'qs';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { Field, propTypes, reduxForm, SubmissionError } from 'redux-form';
import url from 'url';

import { logCouponActivity } from '../../actions/logging';
import { validateActiveCoupon } from '../../actions/subscription';
import coupon from '../../constants/analytics/categoryActionLabel/coupon';
import { ENTER_CODE_UPPERCASE, REDEEM_NOW } from '../../constants/localizations/coupon';
import { COUPON_QUERY_PARAM, SOURCE_QUERY_PARAM } from '../../constants/queryParams';
import { COUPON_FLOW } from '../../constants/subscription/flow';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import createSubscribeLink from '../../utils/subscription/createSubscribeLink';
import PillButton from '../shared/button/PillButton';
import FormInput from '../shared/form/FormInput';
import css from './coupon.module.scss';

const fieldName = 'couponCode';

function validateCouponInput(values) {
  // Required field validation.
  if (!values[fieldName]) {
    return {
      [fieldName]: true,
    };
  }

  return null;
}

class CouponInput extends Component {
  static propTypes = {
    validateCoupon: PropTypes.func.isRequired,
    logActivity: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    history: PropTypes.object.isRequired,
    initialValues: PropTypes.object,
    ...propTypes,
  };

  static contextType = LocationAndLocalizationContext;

  componentDidMount() {
    this.props.logActivity(
      coupon.actions.show,
      this.getCampaign(),
      this.props.initialValues.couponCode,
    );
  }

  getCampaign() {
    return get(this.context.location, ['query', SOURCE_QUERY_PARAM]);
  }

  handleSubmit = ({ [fieldName]: couponCode }) => {
    const { validateCoupon, disabled, history } = this.props;
    const { location } = this.context;
    const { logActivity } = this.props;
    const campaign = this.getCampaign();
    // Log the tap event first
    logActivity(coupon.actions.tap, campaign, couponCode);

    // Backup validation in case the user is able to submit on a disabled input.
    // if (disabled) return null;
    if (disabled) {
      throw new SubmissionError({
        [fieldName]: true,
      });
    }

    // Encoding the coupon code just to escape any malicious characters.
    return validateCoupon(encodeURIComponent(couponCode))
      .catch(() => {
        logActivity(coupon.actions.fail, campaign, couponCode);

        // Enables the error outline to show up on the field.
        throw new SubmissionError({
          [fieldName]: true,
        });
      })
      .then((action) => {
        logActivity(coupon.actions.success, campaign, couponCode);

        const locationDescriptor = createSubscribeLink({
          location,
          source: location.query.source,
          guideId: location.query.profile || location.query.browse,
          additionalParams: {
            // Passing the code as a query param in case the user accidentally refreshes the
            // subscribe page. We can consider doing a coupon code validation on the subscribe for
            // this specific case, in order to revalidate the code, as well as re-obtain the plans
            // that the code pertains to.
            [COUPON_QUERY_PARAM]: couponCode,
            // Will be used by the subscribe modules to identify that a subscription is stemming
            // from the coupon code page.
            [COUPON_FLOW]: true,
          },
        });

        // If the coupon is restricted to specific plans, extract them - they need to be used
        // on the subscribe page, so the user is limited to just these plans.
        const plans = get(action, 'value.data.plans');

        // Since we want to create sku query params that represent an array without brackets and
        // indices (and, atm, not sure we want to make this a universal change in how react-router
        // history handles query param arrays), let's construct the specific search string here,
        // and append it below.
        const skus = !isEmpty(plans) ? `&${qs.stringify({ sku: plans }, { indices: false })}` : '';

        history.push(`${url.format(locationDescriptor)}${skus}`);
      });
  };

  render() {
    const {
      handleSubmit,
      invalid,
      pristine,
      submitting,
      submitSucceeded,
      disabled,
      initialValues,
    } = this.props;
    const { getLocalizedText } = this.context;

    const isSubmitDisabled =
      disabled ||
      invalid ||
      submitting ||
      submitSucceeded ||
      (pristine && !initialValues.couponCode);

    return (
      <form
        data-testid="couponInputForm"
        onSubmit={handleSubmit(this.handleSubmit)}
        className={css.inputWrapper}
      >
        <Field
          name={fieldName}
          id={fieldName}
          dataTestId="couponCodeInput"
          component={FormInput}
          className={css.input}
          placeholder={!initialValues.couponCode ? getLocalizedText(ENTER_CODE_UPPERCASE) : ''}
          disabled={disabled}
        />
        <PillButton
          id="couponSubmit"
          dataTestId="couponSubmit"
          type="submit"
          className={css.button}
          label={getLocalizedText(REDEEM_NOW)}
          isDisabled={isSubmitDisabled}
        />
      </form>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(
      {
        validateCoupon: validateActiveCoupon,
        logActivity: logCouponActivity,
      },
      dispatch,
    ),
  };
}

export default flow(
  reduxForm({
    form: 'coupon',
    validate: validateCouponInput,
    enableReinitialize: true,
  }),
  connect(null, mapDispatchToProps),
  withRouter,
)(CouponInput);
